// API utility functions

const API_BASE = '/api';

// Get stored token
export function getToken() {
  return localStorage.getItem('auth_token');
}

// Set token
export function setToken(token) {
  localStorage.setItem('auth_token', token);
}

// Remove token
export function removeToken() {
  localStorage.removeItem('auth_token');
}

// Generic API request function
async function apiRequest(endpoint, options = {}) {
  const token = getToken();
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  // Handle 401 - unauthorized
  if (response.status === 401) {
    removeToken();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'API request failed');
  }

  return data;
}

// Auth API
export const authApi = {
  login: (email, password) => 
    apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  register: (email, password, name, company) =>
    apiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name, company }),
    }),

  logout: () => {
    removeToken();
    window.location.href = '/login';
  },
};

// Server Configs API
export const configsApi = {
  list: () => apiRequest('/configs'),

  get: (id) => apiRequest(`/configs/${id}`),

  create: (config) =>
    apiRequest('/configs', {
      method: 'POST',
      body: JSON.stringify(config),
    }),

  update: (id, config) =>
    apiRequest(`/configs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(config),
    }),

  delete: (id) =>
    apiRequest(`/configs/${id}`, {
      method: 'DELETE',
    }),
};

// VM Plans API
export const plansApi = {
  list: (configId) => apiRequest(`/configs/${configId}/plans`),

  create: (configId, plan) =>
    apiRequest(`/configs/${configId}/plans`, {
      method: 'POST',
      body: JSON.stringify(plan),
    }),

  bulkUpdate: (configId, plans) =>
    apiRequest(`/configs/${configId}/plans`, {
      method: 'PUT',
      body: JSON.stringify({ plans }),
    }),
};

// Settings API
export const settingsApi = {
  get: () => apiRequest('/settings'),

  update: (settings) =>
    apiRequest('/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),
};

// CloudPE Plans API (pre-defined pricing plans)
export const cloudpePlansApi = {
  list: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.os_type) params.append('os_type', filters.os_type);
    if (filters.plan_type) params.append('plan_type', filters.plan_type);
    if (filters.is_active !== undefined) params.append('is_active', filters.is_active);
    const queryString = params.toString();
    return apiRequest(`/plans${queryString ? `?${queryString}` : ''}`);
  },

  get: (id) => apiRequest(`/plans/${id}`),

  create: (plan) =>
    apiRequest('/plans', {
      method: 'POST',
      body: JSON.stringify(plan),
    }),

  update: (id, plan) =>
    apiRequest(`/plans/${id}`, {
      method: 'PUT',
      body: JSON.stringify(plan),
    }),

  delete: (id) =>
    apiRequest(`/plans/${id}`, {
      method: 'DELETE',
    }),

  import: (plans, clearExisting = false) =>
    apiRequest('/plans/import', {
      method: 'POST',
      body: JSON.stringify({ plans, clearExisting }),
    }),
};

export default {
  auth: authApi,
  configs: configsApi,
  plans: plansApi,
  cloudpePlans: cloudpePlansApi,
  settings: settingsApi,
};
